import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaCalendarAlt, FaGolfBall, FaUser, FaCheck, FaTimes } from 'react-icons/fa';
import { fetchPlayers, fetchCourses, addRound } from '../utils/api';
import './AddRound.css';

const AddRound = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [date, setDate] = useState('');
  const [courseId, setCourseId] = useState('');
  const [playerScores, setPlayerScores] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [playersData, coursesData] = await Promise.all([
          fetchPlayers(),
          fetchCourses()
        ]);
        
        if (playersData.error) {
          setError(playersData.message);
          return;
        }
        
        if (coursesData.error) {
          setError(coursesData.message);
          return;
        }
        
        setPlayers(playersData);
        setCourses(coursesData);
        
        // Initialize playerScores array with all players
        setPlayerScores(
          playersData.map(player => ({
            playerId: player.id,
            playerName: player.name,
            score: '',
            included: false
          }))
        );
        
        // Set today's date as default
        const today = new Date().toISOString().split('T')[0];
        setDate(today);
        
        // Set first course as default if available
        if (coursesData.length > 0) {
          setCourseId(coursesData[0].id.toString());
        }
      } catch (err) {
        setError('Failed to load data. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePlayerScoreChange = (playerId, score) => {
    setPlayerScores(prevScores => 
      prevScores.map(player => 
        player.playerId === playerId 
          ? { ...player, score: score }
          : player
      )
    );
  };

  const handlePlayerIncludedChange = (playerId, included) => {
    setPlayerScores(prevScores => 
      prevScores.map(player => 
        player.playerId === playerId 
          ? { ...player, included }
          : player
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || !courseId) {
      setError('Datum och bana måste väljas.');
      return;
    }

    // Check if we have at least 3 players with scores
    const playersWithScores = playerScores.filter(p => p.included && p.score);
    if (playersWithScores.length < 3) {
      setError('Minst tre spelare måste ha resultat för en giltig runda.');
      return;
    }

    // Validate all included players have scores
    const missingScores = playerScores.some(p => p.included && !p.score);
    if (missingScores) {
      setError('Alla inkluderade spelare måste ha ett resultat.');
      return;
    }

    const roundData = {
      date,
      courseId: parseInt(courseId),
      scores: playerScores
        .filter(p => p.included && p.score)
        .map(p => ({
          playerId: p.playerId,
          score: parseInt(p.score)
        }))
    };

    try {
      setLoading(true);
      const response = await addRound(roundData);
      
      if (response.error) {
        setError(response.message);
      } else {
        setSuccess(true);
        setError(null);
        
        // Reset form
        setTimeout(() => {
          navigate('/rounds');
        }, 2000);
      }
    } catch (err) {
      setError('Failed to add round. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && players.length === 0) {
    return (
      <div className="container">
        <div className="page-title">
          <FaPlus />
          <h1>Lägg till runda</h1>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-title">
        <FaPlus />
        <h1>Lägg till runda</h1>
      </div>

      {error && <div className="error">{error}</div>}
      {success && (
        <div className="success">
          <FaCheck /> Rundan har lagts till! Omdirigerar...
        </div>
      )}

      <form onSubmit={handleSubmit} className="add-round-form card">
        <div className="form-group">
          <label htmlFor="date" className="form-label">
            <FaCalendarAlt /> Datum
          </label>
          <input
            type="date"
            id="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="course" className="form-label">
            <FaGolfBall /> Bana
          </label>
          <select
            id="course"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            className="form-input"
            required
          >
            <option value="">Välj bana...</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
        </div>

        <div className="player-scores-section">
          <h3><FaUser /> Spelares resultat</h3>
          <p className="instructions">Välj spelare som deltog och deras poäng.</p>

          <div className="player-scores-list">
            {playerScores.map(player => (
              <div key={player.playerId} className="player-score-item">
                <div className="player-checkbox">
                  <input
                    type="checkbox"
                    id={`player-${player.playerId}`}
                    checked={player.included}
                    onChange={(e) => handlePlayerIncludedChange(player.playerId, e.target.checked)}
                  />
                  <label htmlFor={`player-${player.playerId}`}>
                    {player.playerName}
                  </label>
                </div>
                <input
                  type="number"
                  disabled={!player.included}
                  value={player.score}
                  onChange={(e) => handlePlayerScoreChange(player.playerId, e.target.value)}
                  placeholder="Score"
                  className="score-input"
                  min="1"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/rounds')}
            className="btn cancel-btn"
          >
            <FaTimes /> Avbryt
          </button>
          <button
            type="submit"
            className="btn submit-btn"
            disabled={loading || success}
          >
            {loading ? <div className="button-spinner"></div> : <FaCheck />}
            {loading ? 'Sparar...' : 'Spara runda'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddRound; 